import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Smartphone, CreditCard, Tag, Check, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useQuote } from "../hooks/useQuote";

const CITIES = ["Accra", "Tema", "Kumasi", "Takoradi", "Tamale", "Cape Coast", "Other"];
const WHATSAPP = "https://wa.me/447512914627";

export default function Checkout() {
  const { items } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [destinations, setDestinations] = useState(["Greater Accra", "Kumasi", "Other regions"]);
  const [destination, setDestination] = useState("Greater Accra");
  const [method, setMethod] = useState("paystack");
  const [promoInput, setPromoInput] = useState(location.state?.promo || "");
  const [promo, setPromo] = useState(location.state?.promo || "");
  const { quote } = useQuote(items, destination, promo);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "", delivery_city: "Accra", delivery_address: "", digital_address: "",
  });

  useEffect(() => {
    api.get("/settings/pricing").then((r) => {
      const names = (r.data?.destinations || []).map((d) => d.name);
      if (names.length) { setDestinations(names); setDestination(names[0]); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.email) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
        customer_phone: f.customer_phone || user.phone || "",
        delivery_city: user.city || f.delivery_city,
        delivery_address: f.delivery_address || user.address || "",
        digital_address: f.digital_address || user.digital_address || "",
      }));
    }
  }, [user]);

  if (items.length === 0) {
    navigate("/cart");
    return null;
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const gh = (n) => `GH₵ ${Math.round(Number(n) || 0).toLocaleString()}`;
  const applied = quote && quote.discount_ghs > 0 && quote.promo_code;
  const needsQuote = quote && quote.needs_quote;

  const submit = async (e) => {
    e.preventDefault();
    if (needsQuote) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/cart", {
        ...form, destination, method, promo_code: promo,
        items: items.map((i) => ({ product_id: i.id, variant_id: i.variant_id || "", quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const input = "w-full bg-white border border-black/10 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#306D29] focus:outline-none";

  const PAYMENTS = [
    { key: "paystack", icon: Smartphone, title: "Mobile Money & Card", sub: "Pay in GH₵ via Paystack" },
    { key: "stripe", icon: CreditCard, title: "International Card", sub: "Pay in £ via Stripe" },
  ];

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-display text-4xl tracking-tight mb-8">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <form onSubmit={submit} className="lg:col-span-3 space-y-4" data-testid="checkout-form">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#306D29]">Delivery details</p>
          <input required data-testid="co-name" className={input} placeholder="Full name" value={form.customer_name} onChange={set("customer_name")} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input required type="email" data-testid="co-email" className={input} placeholder="Email" value={form.customer_email} onChange={set("customer_email")} />
            <input required data-testid="co-phone" className={input} placeholder="Phone (e.g. 024...)" value={form.customer_phone} onChange={set("customer_phone")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-[#5C5C5C]">Delivery region (sets shipping)</label>
              <select data-testid="co-destination" className={input} value={destination} onChange={(e) => setDestination(e.target.value)}>
                {destinations.map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C5C5C]">City / town</label>
              <select data-testid="co-city" className={input} value={form.delivery_city} onChange={set("delivery_city")}>
                {CITIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <textarea required data-testid="co-address" className={input} rows={3} placeholder="Delivery address / landmark" value={form.delivery_address} onChange={set("delivery_address")} />
          <input data-testid="co-digital" className={input} placeholder="Ghana digital address (GhanaPost GPS, e.g. GA-123-4567)" value={form.digital_address} onChange={set("digital_address")} />

          <div className="pt-2" data-testid="payment-methods">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#306D29] mb-2">Payment method</p>
            <div className="grid grid-cols-2 gap-3">
              {PAYMENTS.map((pm) => {
                const active = method === pm.key;
                return (
                  <button type="button" key={pm.key} onClick={() => setMethod(pm.key)} data-testid={`pay-method-${pm.key}`}
                    className={`text-left rounded-2xl border p-4 ${active ? "border-[#306D29] bg-[#306D29]/5 ring-1 ring-[#306D29]" : "border-black/10 bg-white hover:border-black/25"}`}
                    style={{ transition: "border-color 0.15s ease, background-color 0.15s ease" }}>
                    <pm.icon size={20} className="text-[#306D29] mb-1.5" />
                    <p className="font-display text-base text-[#0D530E] leading-tight">{pm.title}</p>
                    <p className="text-xs text-[#5C5C5C]">{pm.sub}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {needsQuote ? (
            <div className="rounded-2xl border border-[#D9933B]/40 bg-[#D9933B]/10 p-4" data-testid="needs-quote">
              <p className="flex items-center gap-2 font-semibold text-[#8a5a12]"><AlertTriangle size={16} /> This order needs a custom quote</p>
              <p className="text-sm text-[#5C5C5C] mt-1">It contains an oversized item or is very large. Message us and we'll confirm the shipping price for you.</p>
              <a href={WHATSAPP} target="_blank" rel="noreferrer" data-testid="quote-whatsapp" className="inline-block mt-3 bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-5 py-2.5 rounded-full text-sm" style={{ transition: "background-color 0.2s ease" }}>Request a quote on WhatsApp</a>
            </div>
          ) : (
            <button type="submit" disabled={loading} data-testid="pay-btn"
              className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ transition: "background-color 0.2s ease" }}>
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />}
              {loading ? "Redirecting to secure payment…" : `Pay ${quote ? gh(quote.total_ghs) : ""} securely`}
            </button>
          )}
          <p className="text-xs text-[#5C5C5C] text-center">
            {method === "paystack" ? "Secured by Paystack — Mobile Money or card in GH₵." : "Secured by Stripe — card in £. Test card: 4242 4242 4242 4242."}
          </p>
        </form>

        <div className="lg:col-span-2 bg-[#E7E1B1] rounded-3xl p-7 h-fit border border-black/5" data-testid="checkout-summary">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#5C5C5C] mb-4">Your order</p>
          <div className="space-y-3 mb-4">
            {items.map((i) => (
              <div key={i.key || i.id} className="flex justify-between text-sm">
                <span className="text-[#5C5C5C]">{i.name} × {i.quantity}</span>
                <span className="font-medium text-right">{gh((i.delivered_gbp * i.quantity) * (quote?.fx_gbp_ghs || 19.5))}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-black/10 pt-4 space-y-2.5">
            <div className="flex justify-between text-sm">
              <span className="text-[#5C5C5C]">Products</span>
              <span className="font-medium text-right" data-testid="co-product-total">{quote ? gh(quote.product_total_ghs) : "—"}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#5C5C5C]">AFRIGATE Service Fee</span>
              <span className="font-medium text-right" data-testid="co-service">{!quote ? "—" : quote.service_quote ? "Quote" : gh(quote.service_ghs)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#5C5C5C]">Shipping · {destination}</span>
              <span className="font-medium text-right" data-testid="co-shipping">{!quote ? "—" : quote.shipping_quote ? "Quote" : gh(quote.shipping_ghs)}</span>
            </div>
            {applied && (
              <div className="flex justify-between text-sm" data-testid="co-discount">
                <span className="text-[#306D29] font-medium flex items-center gap-1"><Tag size={13} /> {quote.promo_code}</span>
                <span className="font-semibold text-right text-[#306D29]">− {gh(quote.discount_ghs)}</span>
              </div>
            )}
          </div>

          <div className="mt-4">
            <div className="flex gap-2">
              <input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Promo code" data-testid="co-promo-input"
                className="flex-1 bg-white border border-black/10 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#306D29]" />
              <button type="button" onClick={() => setPromo(promoInput.trim())} data-testid="co-promo-apply"
                className="bg-[#306D29] text-white font-semibold px-4 py-2 rounded-full text-sm">Apply</button>
            </div>
            {promo && quote && quote.promo_message && !applied && <p className="text-xs text-[#B23B3B] mt-2" data-testid="co-promo-msg">{quote.promo_message}</p>}
            {applied && <p className="text-xs text-[#306D29] mt-2 flex items-center gap-1"><Check size={12} /> Promo applied</p>}
          </div>

          <div className="border-t border-black/10 mt-4 pt-4 flex justify-between items-baseline">
            <span className="font-semibold">Total</span>
            <span className="font-display text-2xl text-[#306D29] font-semibold" data-testid="co-total-ghs">
              {!quote ? "—" : needsQuote ? "Quote" : gh(quote.total_ghs)}
            </span>
          </div>
          <p className="text-xs text-[#5C5C5C] mt-3">Three simple numbers — products at the real UK price, a flat AFRIGATE service fee, and weight-based shipping. Customs included.</p>
        </div>
      </div>
    </div>
  );
}
